// Arrivo da Google Hotel: il backend (rest/GH_landing) rimanda qui con gh=1 e
// date, ospiti, camera e tariffa scelti su Google. La ricerca parte da sola e la
// camera e la tariffa cliccate finiscono in cima, cosi' l'ospite (e il controllo
// prezzi di Google) trova subito il prezzo che ha visto.
//
// Per il controllo prezzi automatico il robot di Google legge i microdata e gli
// attributi data-nav-*: questa pagina e' l'ultima prima dei dati personali
// (data-nav-stage-final) e si marca UNA sola offerta, quella cliccata su Google,
// sul prezzo visibile. Riferimento:
// https://developers.google.com/hotels/hotel-prices/structured-data/generic-crawler-guide
import moment from "moment";
import formService from "../services/form";

/** Simbolo del booking ($theme.eurosign) => codice ISO della valuta */
const VALUTE = {
  "euro-sign": "EUR",
  "dollar-sign": "USD",
  "pound-sign": "GBP",
  "yen-sign": "JPY",
  "lira-sign": "TRY",
  "ruble-sign": "RUB",
  "shekel-sign": "ILS",
  "rupee-sign": "INR"
};

export default {
  computed: {
    // offerta da marcare: la riga cliccata su Google (camera + pacchetto); se il
    // clic era sull'hotel e non su una camera, la piu' economica in vendita
    _ghScelta() {
      if (!this.landing || this.reload || !this.alloggi.length) {
        return null;
      }
      const trovata = this.alloggi.find((all) => all.key === this.landing.camera);
      const tariffa = trovata && trovata.tariffe.find((tar) => tar.key === this.landing.tariffa);
      if (tariffa) {
        return { camera: trovata.key, tariffa: tariffa.key };
      }
      let migliore = null;
      this.alloggi.forEach((all) => {
        all.tariffe.forEach((tar) => {
          if (this._ghVendibile(all, tar) && (!migliore || formService._toFloat(tar.prezzo) < migliore.prezzo)) {
            migliore = { camera: all.key, tariffa: tar.key, prezzo: formService._toFloat(tar.prezzo) };
          }
        });
      });
      return migliore;
    },
    // il robot aspetta che questo elemento sparisca prima di leggere i prezzi
    _ghCaricamento() {
      return !!this.landing && (!this.hotelReady || this.reload);
    },
    _ghValuta() {
      return VALUTE[this.$theme.eurosign] || "EUR";
    },
    _ghEtichetta() {
      const testi = { ita: "Selezionata su Google", ing: "Selected on Google", eng: "Selected on Google", ted: "Auf Google ausgewählt", fra: "Sélectionnée sur Google" };
      return testi[String(this.$store.lang || "").toLowerCase()] || testi.ing;
    }
  },
  methods: {
    // parametri di Google dall'URL, null se non si arriva da Google
    _landingGoogle() {
      const q = this.$route.query;
      if (q.gh !== "1") {
        return null;
      }
      const landing = { periodo: null, adulti: 2, bambini: 0, eta: [], camera: q.camera || "", tariffa: q.tariffa || "" };
      const arrivo = moment(q.arrivo, "DD/MM/YYYY", true);
      const partenza = moment(q.partenza, "DD/MM/YYYY", true);
      if (arrivo.isValid() && partenza.isValid() && partenza.isAfter(arrivo)) {
        landing.periodo = {
          dataarrivo: arrivo.format("DD/MM/YYYY"),
          datapartenza: partenza.format("DD/MM/YYYY"),
          notti: partenza.diff(arrivo, "days")
        };
      }
      if (parseInt(q.adulti) > 0) {
        landing.adulti = parseInt(q.adulti);
      }
      if (parseInt(q.bambini) > 0) {
        landing.bambini = parseInt(q.bambini);
      }
      // eta' dei bambini scelte su Google (eta=7,3)
      if (q.eta) {
        landing.eta = String(q.eta)
          .split(",")
          .map((e) => parseInt(e))
          .filter((e) => !isNaN(e) && e >= 0 && e <= 17);
        landing.bambini = landing.eta.length;
      }
      return landing;
    },
    // imposta periodo e ospiti del form. Con le eta' di Google i bambini oltre
    // l'eta' massima del booking contano come adulti, come li prezza Google;
    // senza eta' (vecchi link) restano all'eta' minima, come per i dati in POST
    _applicaLandingGoogle(landing, form, etaMin, etaMax) {
      if (landing.periodo) {
        form.periodo = { ...landing.periodo };
      }
      form.ospiti = [{ key: 0, adulti: landing.adulti, bambini: [], active: false }];
      if (landing.eta.length) {
        landing.eta.forEach((e) => {
          if (e > etaMax) {
            form.ospiti[0].adulti++;
          } else {
            form.ospiti[0].bambini.push(Math.max(etaMin, e));
          }
        });
      } else {
        for (let i = 0; i < landing.bambini; i++) {
          form.ospiti[0].bambini.push(etaMin);
        }
      }
    },
    // ---- microdata per il controllo prezzi (solo arrivando da Google) ----
    _ghRadice() {
      if (!this.landing) {
        return {};
      }
      return {
        itemscope: "",
        itemtype: "https://schema.org/Hotel https://schema.org/LodgingReservation",
        "data-nav-stage": "landing",
        "data-nav-stage-final": "true"
      };
    },
    _ghIsoData(data) {
      return moment(data, "DD/MM/YYYY").format("YYYY-MM-DD");
    },
    _ghScelto(alloggio, tariffa) {
      const s = this._ghScelta;
      return !!s && s.camera === alloggio.key && s.tariffa === tariffa.key;
    },
    // la riga indicata dal link di Google (camera + pacchetto): si evidenzia
    // solo questa, non la piu' economica scelta quando il clic era sull'hotel
    _ghCliccata(alloggio, tariffa) {
      return !!this.landing && !!this.landing.tariffa && alloggio.key === this.landing.camera && tariffa.key === this.landing.tariffa;
    },
    _ghVendibile(alloggio, tariffa) {
      return !alloggio.disabled && tariffa.disponibili > 0 && String(tariffa.tarattiva) === "1" && formService._toFloat(tariffa.prezzo) > 0;
    },
    _ghOfferta(alloggio, tariffa) {
      return this._ghScelto(alloggio, tariffa) ? { itemprop: "makesOffer", itemscope: "", itemtype: "https://schema.org/Offer" } : {};
    },
    _ghSpecifica(alloggio, tariffa) {
      return this._ghScelto(alloggio, tariffa) && this._ghVendibile(alloggio, tariffa)
        ? { itemprop: "priceSpecification", itemscope: "", itemtype: "https://schema.org/CompoundPriceSpecification" }
        : {};
    },
    _ghPrezzo(alloggio, tariffa) {
      return this._ghScelto(alloggio, tariffa) && this._ghVendibile(alloggio, tariffa) ? { itemprop: "price" } : {};
    },
    // camera e tariffa scelte su Google per prime
    _ordinaLandingGoogle(landing, alloggi) {
      if (!landing || (!landing.camera && !landing.tariffa)) {
        return alloggi;
      }
      // il pacchetto di Google e' la chiave della riga (F119-SS); prima la riga esatta, poi le altre della stessa tariffa
      const peso = (tar) => {
        const key = String(tar.key || "");
        if (key === landing.tariffa) {
          return 0;
        }
        return key.split("-")[0] === landing.tariffa.split("-")[0] ? 1 : 2;
      };
      alloggi.forEach((all) => {
        all.tariffe = [...all.tariffe].sort((a, b) => peso(a) - peso(b));
      });
      return [...alloggi.filter((all) => all.key === landing.camera), ...alloggi.filter((all) => all.key !== landing.camera)];
    }
  }
};
